/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * Tabs of the connection page. A connection has one configuration: this server's (ADR-018). The
 * dialogs say which server this is (OT&E or production) in a banner.
 */

import { Ban, CircleCheck, CircleMinus, CircleX, FileClock, FileKey2, Globe2, Network, Pencil, Play, Plus, ShieldCheck, Trash2, Upload, Wand2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { api } from '../../../api';
import type {
  CertificateVersion,
  ConnectionDetail,
  CredentialVersion,
  EnvironmentDetail,
  EnvironmentVersion,
  EppSettings,
  IntegrationEnvironment,
  ReadinessCheck,
} from '../../../api/areas/integrations';
import { Badge } from '../../../components/Badge';
import { Callout, DataTable, StatCard, StatGrid, type Column } from '../../../components/blocks';
import { TwoColumn } from '../../../components/flow';
import { DataError } from '../../../components/DataError';
import { CopyText, EmptyState, Loading } from '../../../components/States';
import { KeyValue } from '../../../components/KeyValue';
import { useRetained } from '../../../components/Write';
import { ReasonAction } from '../../operator/shared';
import { formatCount, formatDateTime } from '../../../lib/format';
import { useApi } from '../../../lib/useApi';
import {
  AppliedByList,
  ChannelBadge,
  channelOf,
  EnvBadge,
  EnvironmentBanner,
  FieldChanges,
  OnOffBadge,
  Panel,
  SecretStatusLine,
  StaffName,
  StatusBadge,
  tx,
  useIntegrationContext,
  When,
  YesNo,
} from '../shared';
import { ExercisePanel } from '../exercise';
import { ConnectionTestDialog, LastTest, TestList } from '../tests';
import { isWebnic } from '../presets';
import { CertificateDialog, CredentialDialog, DestroySecretDialog, EditConnectionDialog, EnvironmentDialog, Pkcs12Dialog, RevokeDialog, RootCaDialog, type EnvironmentSection } from './dialogs';
import { BalanceCard, DomainsCard } from './account';
import { WebnicAllowListNote } from './webnic';

export interface TabProps {
  connection: ConnectionDetail;
  env: IntegrationEnvironment;
  detail: EnvironmentDetail | undefined;
  refresh: () => void;
  /** Link to another tab of this connection. */
  tabLink: (tab: string) => string;
}

// ---------------------------------------------------------------------------
// Overview

function ReadinessList({ checks }: { checks: readonly ReadinessCheck[] }) {
  const { t } = useTranslation('integrations');
  return (
    <ul className="grid gap-1 text-sm">
      {checks
        .filter((c) => c.applicable)
        .map((c) => (
          <li key={c.code} className="flex items-start gap-2">
            {c.ok ? <CircleCheck className="mt-0.5 size-4 shrink-0 text-success" aria-hidden="true" /> : <CircleX className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />}
            <span className={cn(!c.ok && 'font-medium')}>
              {t(`readiness.${c.code}`)}
              <span className="sr-only"> — {c.ok ? t('common.yes') : t('common.no')}</span>
            </span>
          </li>
        ))}
    </ul>
  );
}

type Role = 'REGISTRAR_OF_RECORD' | 'UPSTREAM_REGISTRAR';

/** Registrar of record vs. upstream registrar: who runs the RAA duties (changed with a reason). */
function RolePanel({ connectionId, role, onChanged }: { connectionId: string; role: Role; onChanged: () => void }) {
  const { t } = useTranslation('integrations');
  const other: Role = role === 'REGISTRAR_OF_RECORD' ? 'UPSTREAM_REGISTRAR' : 'REGISTRAR_OF_RECORD';
  return (
    <Panel
      title={t('role.title')}
      description={t('role.hint')}
      actions={
        <ReasonAction
          label={t('role.change')}
          title={t('role.changeTitle', { role: t(`role.value.${other}`) })}
          warning={t(`role.effect.${other}`)}
          submit={t('role.changeTo', { role: t(`role.value.${other}`) })}
          run={(reason, opts) => api.setConnectionRole(connectionId, { role: other, reason }, opts)}
          done={() => t('role.changed')}
          onChanged={onChanged}
        />
      }
    >
      <KeyValue
        columns={1}
        rows={[
          [t('role.current'), <Badge key="r" tone={role === 'UPSTREAM_REGISTRAR' ? 'info' : 'ok'}>{t(`role.value.${role}`)}</Badge>],
          [t('role.duties'), <span key="d" className="text-sm">{t(`role.effect.${role}`)}</span>],
        ]}
      />
    </Panel>
  );
}

export function OverviewTab({ connection: c, env, refresh, tabLink }: TabProps) {
  const { t } = useTranslation('integrations');
  const [editing, setEditing] = useState(false);
  const s = c.settings;
  const flags = c.capabilities ? Object.entries(c.capabilities.flags) : [];
  return (
    <div className="space-y-6">
      {c.adapter_status === 'NOT_AVAILABLE_NEEDS_SPECIFICATION' ? (
        <Callout tone="warn" title={t('adapterNotice.title')}>
          {t('adapterNotice.body')}
        </Callout>
      ) : null}
      {isWebnic(c) ? (
        <Callout tone="info" title={t('webnic.overviewTitle')}>
          <p>{t('webnic.overview')}</p>
          <p className="mt-1">{t('webnic.allowList')}</p>
        </Callout>
      ) : null}
      <StatGrid>
        <StatCard
          label={t('connectionTiles.tlds')}
          value={formatCount(c.tld_counts.configured + c.tld_counts.by_default)}
          icon={Network}
          hint={t('connectionTiles.tldsHint', { own: c.tld_counts.configured, inherited: c.tld_counts.by_default })}
        />
        <StatCard label={t('connectionTiles.domains')} value={formatCount(c.domains_held)} icon={Globe2} />
        <StatCard label={t('connectionTiles.changes')} value={formatCount(c.open_changes)} icon={FileClock} tone={c.open_changes ? 'pending' : 'neutral'} />
        {c.protocol === 'EPP' && s?.certificate ? (
          <StatCard
            label={t('connectionTiles.certificate')}
            value={s.certificate.configured && s.certificate.days_remaining !== null ? t('health.certificateDays', { count: s.certificate.days_remaining }) : t('health.noCertificate')}
            icon={ShieldCheck}
            tone={!s.certificate.configured || (s.certificate.days_remaining ?? 0) < 30 ? 'warn' : 'neutral'}
          />
        ) : (
          <StatCard label={t('connectionTiles.lastTest')} value={<span className="text-base">{s ? <LastTest test={s.last_test} /> : '—'}</span>} icon={ShieldCheck} />
        )}
      </StatGrid>
      <TwoColumn
        main={
          <>
            <Panel
              title={t('overview.identity')}
              actions={
                <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                  <Pencil /> {t('overview.edit')}
                </Button>
              }
            >
              <KeyValue
                columns={3}
                rows={[
                  [t('overview.name'), c.name],
                  [t('overview.code'), <code key="c" className="font-mono text-sm">{c.code}</code>],
                  [t('overview.kind'), t(`kind.${c.kind}`)],
                  [t('overview.channel'), <ChannelBadge key="ch" channel={channelOf(c.kind, c.protocol)} />],
                  [t('overview.protocol'), t(`protocol.${c.protocol}`)],
                  [t('overview.adapter'), tx(`adapter.${c.adapter}`, c.adapter)],
                  [t('overview.status'), <StatusBadge key="s" group="connectionStatus" value={c.status} />],
                  [t('overview.spec'), c.api_spec_reference ?? <span key="spec" className="text-muted-foreground">{t('overview.noSpec')}</span>],
                  [t('overview.inProductionUse'), <YesNo key="p" value={c.in_production_use} />],
                  [t('overview.domainsHeld'), formatCount(c.domains_held)],
                  [t('overview.created'), <When key="cr" at={c.created_at} />],
                  [t('overview.updated'), <When key="up" at={c.updated_at} />],
                ]}
              />
              {c.in_production_use ? <p className="mt-3 text-xs text-muted-foreground">{t('overview.inProductionUseHint')}</p> : null}
            </Panel>

            <Panel title={t('overview.environments')} description={t('overview.environmentsHint')}>
              <div className={cn('max-w-3xl space-y-3 rounded-lg border p-4', env === 'PRODUCTION' && 'border-critical/30')}>
                <div className="flex flex-wrap items-center gap-2">
                  <EnvBadge env={env} long />
                  {s ? <OnOffBadge on={s.enabled} /> : <Badge tone="neutral">{t('connections.envNotConfigured')}</Badge>}
                  {s ? <Badge tone={s.ready ? 'ok' : 'warn'}>{s.ready ? t('connections.ready') : t('connections.notReady')}</Badge> : null}
                </div>
                {s?.endpoint ? <div className="break-all font-mono text-xs">{s.endpoint}</div> : null}
                {s ? <ReadinessList checks={c.readiness} /> : <p className="text-sm text-muted-foreground">{t('overview.envMissing')}</p>}
                {s ? (
                  <div>
                    <LastTest test={s.last_test} />
                  </div>
                ) : null}
                <div>
                  <Button asChild size="sm" variant="outline">
                    <Link to={tabLink('environments')}>{s ? t('overview.openEnv') : t('overview.configureEnv')}</Link>
                  </Button>
                </div>
              </div>
            </Panel>

            <Panel
              title={t('overview.tlds')}
              description={t('overview.tldsHint')}
              actions={
                <Button asChild size="sm" variant="outline">
                  <Link to={`/settings/tlds?connection=${encodeURIComponent(c.id)}`}>{t('overview.toTlds')}</Link>
                </Button>
              }
            >
              {c.channels.length ? (
                <ul className="flex flex-wrap gap-1.5">
                  {c.channels.map((x) => (
                    <li key={x.tld_id}>
                      <Badge tone={x.source === 'CONFIGURED' ? 'info' : 'neutral'} icon={false} title={t(`source.${x.source}`)}>
                        .{x.zone}
                        {x.source === 'DEFAULT' ? <span className="text-[10px] opacity-70">({t('source.DEFAULT')})</span> : null}
                      </Badge>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">{t('overview.noTlds')}</p>
              )}
            </Panel>

            {c.kind === 'REGISTRY' ? (
              <Panel title={t('overview.capabilities')} description={t('overview.capabilitiesHint')}>
                {c.capabilities ? (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">{t('overview.capabilitiesVersion', { version: c.capabilities.version, from: formatDateTime(c.capabilities.effective_from) })}</p>
                    <ul className="flex flex-wrap gap-1.5">
                      {flags.map(([k, v]) => (
                        <li key={k}>
                          <Badge tone={v === false ? 'neutral' : 'ok'} icon={false}>
                            {tx(`capability.${k}`, k)}
                            {typeof v === 'number' ? `: ${v}` : v === false ? ` — ${t('common.no')}` : ''}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('overview.noCapabilities')}</p>
                )}
              </Panel>
            ) : null}

          </>
        }
        side={
          <>
            <RolePanel connectionId={c.id} role={c.role ?? 'REGISTRAR_OF_RECORD'} onChanged={refresh} />

            {c.upstream || c.sponsoring_registrar ? (
              <Panel title={t('overview.sponsorTitle')} description={t('overview.sponsorDescription')}>
                <KeyValue
                  columns={1}
                  rows={[
                    c.upstream ? [t('upstream.legalName'), c.upstream.legal_name] : false,
                    [t('upstream.tradeName'), c.sponsoring_registrar?.trade_name ?? c.upstream?.trade_name ?? '—'],
                    [t('upstream.ianaId'), c.sponsoring_registrar?.iana_id ?? c.upstream?.iana_id ?? '—'],
                    c.sponsoring_registrar ? [t('overview.sponsorSource'), t(`sponsor.source.${c.sponsoring_registrar.source}`)] : false,
                  ]}
                />
                <p className="mt-3 text-xs text-muted-foreground">
                  {c.kind === 'UPSTREAM_REGISTRAR' ? t('overview.sponsorUpstream') : t('overview.sponsorMapping')}{' '}
                  <Link to="/settings/vnnic?tab=gtld" className="font-medium text-primary hover:underline">
                    {t('overview.toSponsors')}
                  </Link>
                </p>
              </Panel>
            ) : null}

          </>
        }
      />
      <EditConnectionDialog open={editing} onOpenChange={setEditing} connection={c} onDone={refresh} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Environment (address) tab

function EppSummary({ epp }: { epp: EppSettings }) {
  const { t } = useTranslation('integrations');
  return (
    <KeyValue
      columns={3}
      rows={[
        [t('fields.epp_host'), <code key="h" className="break-all font-mono text-sm">{epp.host}</code>],
        [t('fields.epp_port'), epp.port],
        [t('fields.epp_tls'), <YesNo key="tls" value={epp.tls} />],
        [t('fields.epp_server_name'), epp.server_name ?? <span key="sn" className="text-muted-foreground">{t('envTab.sameAsHost')}</span>],
        [t('fields.epp_ca_pem'), epp.ca_pem ? t('envTab.extraCa') : <span key="ca" className="text-muted-foreground">{t('envTab.systemCa')}</span>],
      ]}
    />
  );
}

function VersionHistory({ connectionId, env, tick }: { connectionId: string; env: IntegrationEnvironment; tick: number }) {
  const { t } = useTranslation('integrations');
  const [before, setBefore] = useState<number[]>([]);
  const cursor = before[before.length - 1];
  const result = useApi((signal) => api.listEnvironmentVersions(connectionId, { before_version: cursor, limit: 10 }, { signal }), [connectionId, env, cursor, tick]);
  const data = useRetained(result.data);
  const columns: Column<EnvironmentVersion>[] = [
    { key: 'v', header: t('versions.version'), cell: (v) => (v.version === 0 ? t('versions.version0') : v.version) },
    { key: 's', header: t('versions.status'), cell: (v) => <StatusBadge group="versionStatus" value={v.status} /> },
    { key: 'e', header: t('versions.enabled'), cell: (v) => <OnOffBadge on={v.enabled} /> },
    {
      key: 'a',
      header: t('versions.endpoint'),
      cell: (v) => <span className="break-all font-mono text-xs">{v.epp ? `${v.epp.host}:${v.epp.port}` : (v.rest?.base_url ?? '—')}</span>,
    },
    { key: 'r', header: t('versions.reason'), cell: (v) => v.reason ?? <span className="text-muted-foreground">—</span>, className: 'min-w-48' },
    {
      key: 'by',
      header: t('versions.createdBy'),
      cell: (v) => (
        <div className="grid gap-0.5 text-xs">
          <StaffName staff={v.created_by} />
          <When at={v.created_at} />
        </div>
      ),
    },
    {
      key: 'act',
      header: t('versions.activated'),
      cell: (v) =>
        v.activated_at ? (
          <div className="grid gap-0.5 text-xs">
            <StaffName staff={v.activated_by} />
            <When at={v.activated_at} />
          </div>
        ) : (
          '—'
        ),
    },
  ];
  if (result.status === 'error') return <DataError error={result.error} onRetry={result.reload} />;
  if (!data) return <Loading rows={2} />;
  return (
    <>
      <DataTable columns={columns} rows={data.items} rowKey={(v) => `${v.version}`} empty={<p className="px-1 text-sm text-muted-foreground">{t('versions.none')}</p>} />
      {data.next_before_version !== null || before.length ? (
        <div className="flex justify-end gap-2 border-t px-4 py-3">
          <Button size="sm" variant="outline" disabled={!before.length} onClick={() => setBefore((b) => b.slice(0, -1))}>
            {t('common.newer')}
          </Button>
          <Button size="sm" variant="outline" disabled={data.next_before_version === null} onClick={() => data.next_before_version !== null && setBefore((b) => [...b, data.next_before_version as number])}>
            {t('common.older')}
          </Button>
        </div>
      ) : null}
    </>
  );
}

function openProposal(detail: EnvironmentDetail): boolean {
  return !!detail.pending && !!detail.pending.approval && (detail.pending.approval.status === 'PENDING' || detail.pending.approval.status === 'APPROVED');
}

export function EnvironmentTab({ connection: c, env, detail, refresh }: TabProps) {
  const { t } = useTranslation('integrations');
  const [params, setParams] = useSearchParams();
  // `?setup=endpoint` (right after a connection was added): open the address form at once.
  const [dialog, setDialogState] = useState<EnvironmentSection | 'test' | null>(() => (params.get('setup') === 'endpoint' ? 'endpoint' : null));
  const setDialog = (d: EnvironmentSection | 'test' | null) => {
    setDialogState(d);
    if (d === null && params.has('setup')) {
      const next = new URLSearchParams(params);
      next.delete('setup');
      setParams(next, { replace: true });
    }
  };
  const webnic = isWebnic(c);
  const [tick, setTick] = useState(0);
  if (!detail) return <Loading rows={3} />;
  const active = detail.active;
  const blocked = openProposal(detail);
  const done = () => {
    setTick((n) => n + 1);
    refresh();
  };
  return (
    <div className="space-y-6">
      <EnvironmentBanner env={env} />
      {blocked && detail.pending ? (
        <Callout tone="pending" title={t('envTab.pendingTitle', { version: detail.pending.version })}>
          <p>{t('envTab.pendingBody')}</p>
          {active ? (
            <div className="mt-2">
              <FieldChanges changes={diffVersions(active, detail.pending)} compact />
            </div>
          ) : null}
        </Callout>
      ) : null}
      <Panel
        title={t('envTab.addressTitle')}
        actions={
          <>
            <Button size="sm" variant="outline" onClick={() => setDialog('test')} disabled={!active && !detail.pending}>
              <Play /> {t('envTab.test')}
            </Button>
            <Button size="sm" onClick={() => setDialog('endpoint')} disabled={blocked || c.status === 'RETIRED'} title={blocked ? t('envTab.blocked') : undefined}>
              <Pencil /> {detail.exists ? t('envTab.edit') : t('envTab.configure')}
            </Button>
          </>
        }
      >
        {!detail.exists || !active ? (
          <EmptyState title={t('envTab.notConfigured', { env: t(`env.inline.${env}`) })}>
            {c.adapter === 'vnnic-epp' ? t('envTab.notConfiguredVnnic') : t('envTab.notConfiguredHint')}
          </EmptyState>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <OnOffBadge on={active.enabled} />
              <span className="text-muted-foreground">
                {active.version === 0 ? t('versions.version0Long') : t('envTab.versionInForce', { version: active.version })}
              </span>
              {active.activated_at ? (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  {active.activated_by ? (
                    <>
                      <StaffName staff={active.activated_by} /> ·
                    </>
                  ) : null}
                  <When at={active.activated_at} />
                </span>
              ) : null}
            </div>
            {active.epp ? <EppSummary epp={active.epp} /> : null}
            {active.rest ? (
              <KeyValue
                columns={2}
                rows={[
                  [t('fields.rest_base_url'), <code key="u" className="break-all font-mono text-sm">{active.rest.base_url}</code>],
                  [t('fields.rest_auth_method'), webnic && active.rest.auth_method === 'ADAPTER_DEFINED' ? t('webnic.authMethod') : t(`authMethod.${active.rest.auth_method}`)],
                  active.rest.auth_settings.api_key_header ? [t('fields.rest_auth_settings_api_key_header'), <code key="h">{active.rest.auth_settings.api_key_header}</code>] : false,
                  active.rest.auth_settings.token_url ? [t('fields.rest_auth_settings_token_url'), <code key="tu" className="break-all">{active.rest.auth_settings.token_url}</code>] : false,
                  active.rest.auth_settings.scope ? [t('fields.rest_auth_settings_scope'), active.rest.auth_settings.scope] : false,
                  [t('fields.rest_account_id'), active.rest.account_id ?? '—'],
                ]}
              />
            ) : null}
            {active.reason ? (
              <p className="text-sm">
                <span className="text-muted-foreground">{t('versions.reason')}:</span> {active.reason}
              </p>
            ) : null}
          </div>
        )}
      </Panel>

      {c.account_features?.balance || webnic ? (
        <div className="grid gap-6 lg:grid-cols-2 [&>*]:min-w-0">
          {c.account_features?.balance ? <BalanceCard connection={c} detail={detail} /> : null}
          {webnic ? <WebnicAllowListNote className="self-start" /> : null}
        </div>
      ) : null}
      {c.account_features?.domain_list ? <DomainsCard connection={c} detail={detail} /> : null}

      <div className="grid gap-6 lg:grid-cols-2 [&>*]:min-w-0">
        <Panel title={t('envTab.readiness')}>
          <ReadinessList checks={detail.readiness} />
        </Panel>
        <Panel title={t('envTab.applied')} description={t('envTab.appliedHint')}>
          <AppliedByList items={detail.applied_by} activeVersion={active?.version ?? null} />
        </Panel>
      </div>

      <Panel title={t('envTab.tests')} description={t('envTab.testsHint')}>
        <TestList tests={detail.recent_tests} />
      </Panel>

      <Panel title={t('envTab.history')} flush>
        <VersionHistory connectionId={c.id} env={env} tick={tick} />
      </Panel>

      <EnvironmentDialog open={dialog === 'endpoint'} onOpenChange={(o) => setDialog(o ? 'endpoint' : null)} connection={c} env={env} detail={detail} section="endpoint" onDone={done} />
      <ConnectionTestDialog open={dialog === 'test'} onOpenChange={(o) => setDialog(o ? 'test' : null)} connectionId={c.id} protocol={c.protocol} adapter={c.adapter} env={env} detail={detail} onTested={done} />
    </div>
  );
}

function diffVersions(a: EnvironmentVersion, b: EnvironmentVersion) {
  const flat = (v: EnvironmentVersion) => {
    const out: Record<string, unknown> = {};
    const walk = (prefix: string, value: unknown) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) for (const [k, x] of Object.entries(value)) walk(prefix ? `${prefix}.${k}` : k, x);
      else out[prefix] = value;
    };
    walk('', { enabled: v.enabled, epp: v.epp, rest: v.rest });
    return out;
  };
  const x = flat(a);
  const y = flat(b);
  return [...new Set([...Object.keys(x), ...Object.keys(y)])]
    .filter((k) => JSON.stringify(x[k] ?? null) !== JSON.stringify(y[k] ?? null))
    .map((field) => ({ field, kind: 'VALUE' as const, before: x[field] ?? null, after: y[field] ?? null }));
}

// ---------------------------------------------------------------------------
// Tests: connection checks and, in OT&E, the business test

export function TestsTab({ connection: c, env, detail, refresh }: TabProps) {
  const { t } = useTranslation('integrations');
  const [testing, setTesting] = useState(false);
  if (!detail) return <Loading rows={3} />;
  return (
    <div className="space-y-6">
      <EnvironmentBanner env={env} />
      <ExercisePanel connection={c} env={env} />
      <Panel
        title={t('envTab.tests')}
        description={t('envTab.testsHint')}
        actions={
          <Button size="sm" variant="outline" onClick={() => setTesting(true)} disabled={!detail.active && !detail.pending}>
            <Play /> {t('envTab.test')}
          </Button>
        }
      >
        <TestList tests={detail.recent_tests} />
      </Panel>
      <ConnectionTestDialog open={testing} onOpenChange={setTesting} connectionId={c.id} protocol={c.protocol} adapter={c.adapter} env={env} detail={detail} onTested={refresh} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Connection settings (sessions, rate, timeouts)

export function SettingsTab({ connection: c, env, detail, refresh }: TabProps) {
  const { t } = useTranslation('integrations');
  const [editing, setEditing] = useState(false);
  if (!detail) return <Loading rows={3} />;
  const active = detail.active;
  const blocked = openProposal(detail);
  const epp = active?.epp;
  const rest = active?.rest;
  const sessions = epp ? Object.values(epp.classes).reduce((n, x) => n + (x?.sessions ?? 0), 0) : 0;
  const ms = (v: number | null) => (v === null ? t('settingsTab.noLimit') : `${formatCount(v)} ms`);
  return (
    <div className="space-y-6">
      <EnvironmentBanner env={env} />
      <Panel
        title={t('settingsTab.title')}
        description={t('settingsTab.description')}
        actions={
          <Button size="sm" onClick={() => setEditing(true)} disabled={!active || blocked || c.status === 'RETIRED'} title={blocked ? t('envTab.blocked') : undefined}>
            <Pencil /> {t('settingsTab.edit')}
          </Button>
        }
      >
        {blocked ? <p className="mb-4 text-xs text-muted-foreground">{t('envTab.blocked')}</p> : null}
        {!active ? (
          <EmptyState title={t('envTab.notConfigured', { env: t(`env.inline.${env}`) })}>{t('settingsTab.configureFirst')}</EmptyState>
        ) : epp ? (
          <div className="space-y-5">
            <KeyValue
              columns={3}
              rows={[
                [t('fields.epp_connect_timeout_ms'), ms(epp.connect_timeout_ms)],
                [t('fields.epp_command_timeout_ms'), ms(epp.command_timeout_ms)],
                [t('fields.epp_acquire_timeout_ms'), ms(epp.acquire_timeout_ms)],
                [t('fields.epp_failure_threshold'), t('settingsTab.failures', { count: epp.failure_threshold })],
                [t('fields.epp_open_for_ms'), ms(epp.open_for_ms)],
                [t('fields.epp_max_commands_per_session'), epp.max_commands_per_session === null ? t('settingsTab.noLimit') : formatCount(epp.max_commands_per_session)],
                [t('fields.epp_max_session_idle_ms'), ms(epp.max_session_idle_ms)],
                [t('fields.epp_max_session_lifetime_ms'), ms(epp.max_session_lifetime_ms)],
              ]}
            />
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">{t('settingsTab.pools')}</h3>
              <p className="text-xs text-muted-foreground">{t('settingsTab.poolsHint')}</p>
              {Object.keys(epp.classes).length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('settingsTab.defaultPool')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-96 text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="py-2 pr-3 font-medium">{t('settingsTab.class')}</th>
                        <th className="py-2 pr-3 text-right font-medium">{t('fields.class_sessions')}</th>
                        <th className="py-2 pr-3 text-right font-medium">{t('fields.class_requests_per_second')}</th>
                        <th className="py-2 text-right font-medium">{t('fields.class_burst')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(epp.classes).map(([cls, b]) =>
                        b ? (
                          <tr key={cls} className="border-b last:border-0">
                            <td className="py-2 pr-3">{tx(`trafficClass.${cls}`, cls)}</td>
                            <td className="py-2 pr-3 text-right tabular">{b.sessions}</td>
                            <td className="py-2 pr-3 text-right tabular">{b.requests_per_second}</td>
                            <td className="py-2 text-right tabular">{b.burst}</td>
                          </tr>
                        ) : null,
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-xs text-muted-foreground">{t('envDialog.totalSessions', { count: sessions })}</p>
            </div>
          </div>
        ) : rest ? (
          <KeyValue
            columns={2}
            rows={[
              [t('fields.rest_connect_timeout_ms'), ms(rest.connect_timeout_ms)],
              [t('fields.rest_request_timeout_ms'), ms(rest.request_timeout_ms)],
            ]}
          />
        ) : null}
      </Panel>
      <EnvironmentDialog open={editing} onOpenChange={setEditing} connection={c} env={env} detail={detail} section="settings" onDone={refresh} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Credentials

function ClosedNote({ item }: { item: CredentialVersion | CertificateVersion }) {
  const { t } = useTranslation('integrations');
  if (!item.closed_reason) return null;
  return <span className="block text-xs text-muted-foreground">{t(`closedReason.${item.closed_reason}`)}</span>;
}

function ApprovalNote({ item }: { item: { approval: CredentialVersion['approval'] } }) {
  if (!item.approval) return null;
  return <StatusBadge group="approvalStatus" value={item.approval.status} />;
}

type SecretAction = { kind: 'revoke'; item: CredentialVersion | CertificateVersion } | { kind: 'destroy'; id: string; what: string } | null;

export function CredentialsTab({ connection: c, env, detail, refresh }: TabProps) {
  const { t } = useTranslation('integrations');
  const [adding, setAdding] = useState(false);
  const [action, setAction] = useState<SecretAction>(null);
  if (!detail) return <Loading rows={3} />;
  const pending = detail.credentials.some((x) => x.status === 'PENDING' && x.approval && (x.approval.status === 'PENDING' || x.approval.status === 'APPROVED'));
  return (
    <div className="space-y-6">
      <EnvironmentBanner env={env} />
      <Panel
        title={t('credentials.title')}
        description={c.protocol === 'EPP' ? t('credentials.descriptionEpp') : t('credentials.descriptionRest')}
        actions={
          <Button size="sm" onClick={() => setAdding(true)} disabled={!detail.exists || pending || c.status === 'RETIRED'} title={pending ? t('envTab.blocked') : !detail.exists ? t('credentials.needsEnvironment') : undefined}>
            <Plus /> {t('credentials.add')}
          </Button>
        }
        flush
      >
        {!detail.exists ? <p className="px-5 pb-5 text-sm text-muted-foreground">{t('credentials.needsEnvironment')}</p> : null}
        {pending ? <p className="px-5 pb-3 text-xs text-muted-foreground">{t('envTab.blocked')}</p> : null}
        {c.protocol === 'REST' ? <p className="px-5 pb-3 text-xs text-muted-foreground">{t('credentials.restNotVerified')}</p> : null}
        <div className="px-5 pb-5">
          {detail.credentials.length === 0 ? (
            <EmptyState title={t('credentials.none')} />
          ) : (
            <ul className="grid gap-3">
              {detail.credentials.map((x) => (
                <li key={x.version} className={cn('space-y-3 rounded-lg border p-4', x.status === 'ACTIVE' && 'border-success/40')}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{t('versions.versionN', { version: x.version })}</span>
                      <StatusBadge group="credentialStatus" value={x.status} />
                      <ApprovalNote item={x} />
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {x.status !== 'REVOKED' ? (
                        <Button size="xs" variant="outline" onClick={() => setAction({ kind: 'revoke', item: x })}>
                          <Ban /> {t('credentials.revoke')}
                        </Button>
                      ) : null}
                      {x.secret.source === 'STORED' && x.secret.stored_secret_id && !x.secret.destroyed && (x.status === 'ROTATED' || x.status === 'REVOKED') ? (
                        <Button size="xs" variant="ghost" onClick={() => setAction({ kind: 'destroy', id: x.secret.stored_secret_id ?? '', what: t('credentials.destroyWhat', { version: x.version }) })}>
                          <Trash2 /> {t('credentials.destroy')}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                  <ClosedNote item={x} />
                  <KeyValue
                    columns={2}
                    rows={[
                      [
                        c.protocol === 'EPP' ? t('credDialog.eppClientId') : isWebnic(c) ? t('webnic.username') : t('credentials.clientId'),
                        x.client_id === null ? (
                          <span key="id" className="text-muted-foreground">{t('credentials.hidden')}</span>
                        ) : x.client_id ? (
                          <code key="id" className="font-mono text-sm">{x.client_id}</code>
                        ) : (
                          '—'
                        ),
                      ],
                      [isWebnic(c) ? t('webnic.secret') : t('credentials.secret'), <SecretStatusLine key="s" status={x.secret} />],
                    ]}
                  />
                  <p className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                    {t('versions.createdBy')}: {x.created_by ? <StaffName staff={x.created_by} /> : t('common.outsideControlCenter')} · <When at={x.created_at} />
                    {x.reason ? ` · ${x.reason}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Panel>
      <CredentialDialog open={adding} onOpenChange={setAdding} connection={c} env={env} detail={detail} onDone={refresh} />
      {action?.kind === 'revoke' ? (
        <RevokeDialog open onOpenChange={(o) => !o && setAction(null)} what="credential" item={action.item} connectionId={c.id} env={env} onDone={refresh} />
      ) : null}
      {action?.kind === 'destroy' ? <DestroySecretDialog open onOpenChange={(o) => !o && setAction(null)} storedSecretId={action.id} what={action.what} onDone={refresh} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Certificates

/** VNNIC's published EPP address for this environment, with a one-click set-up when the
 * environment does not use it yet (the certificate needs an address to belong to). */
function VnnicPresetPanel({ connection: c, env, detail, onSetUp }: { connection: ConnectionDetail; env: IntegrationEnvironment; detail: EnvironmentDetail; onSetUp: () => void }) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  const preset = c.adapter === 'vnnic-epp' ? ctx.epp_presets.find((p) => p.environment === env) : undefined;
  if (!preset) return null;
  const epp = detail.active?.epp;
  const inUse = !!epp && epp.host === preset.settings.host && epp.port === preset.settings.port && epp.tls;
  const blocked = openProposal(detail) || c.status === 'RETIRED';
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-info/30 bg-info-soft p-3 text-sm">
      <div className="min-w-0 space-y-0.5">
        <p className="font-medium">{t('vnnicPreset.title', { env: t(`env.inline.${env}`) })}</p>
        <p>
          <code className="rounded bg-background px-1 text-xs break-all">{`${preset.settings.host}:${preset.settings.port}`}</code> · {t('vnnicPreset.tls')}
          {inUse ? <span className="ml-2 text-xs text-success">{t('envDialog.presetInUse')}</span> : null}
        </p>
        <p className="text-xs text-muted-foreground">{inUse ? t('vnnicPreset.inUseHint') : detail.exists ? t('vnnicPreset.differentHint') : t('vnnicPreset.missingHint')}</p>
      </div>
      {inUse ? null : (
        <Button type="button" size="sm" variant={detail.exists ? 'outline' : 'default'} onClick={onSetUp} disabled={blocked} title={blocked ? t('envTab.blocked') : undefined} className="h-auto max-w-full py-1.5 text-left whitespace-normal">
          <Wand2 /> {t('vnnicPreset.setUp')}
        </Button>
      )}
    </div>
  );
}

export function CertificatesTab({ connection: c, env, detail, refresh }: TabProps) {
  const { t } = useTranslation('integrations');
  const [adding, setAdding] = useState<'pem' | 'pkcs12' | 'preset' | 'rootCa' | null>(null);
  const [fromChain, setFromChain] = useState(false);
  const [action, setAction] = useState<SecretAction>(null);
  if (!detail) return <Loading rows={3} />;
  const certs = detail.certificates ?? [];
  const caCerts = detail.active?.epp?.ca_certificates ?? [];
  // CA certificates carried in the active client certificate's chain (e.g. from a .p12).
  const activeCert = certs.find((x) => x.status === 'ACTIVE');
  const chainCas = (activeCert?.certificate_pem.match(/-----BEGIN CERTIFICATE-----[\s\S]*?-----END CERTIFICATE-----/g) ?? []).slice(1);
  const pending = certs.some((x) => x.status === 'PENDING' && x.approval && (x.approval.status === 'PENDING' || x.approval.status === 'APPROVED'));
  const cannotAdd = !detail.exists || pending || c.status === 'RETIRED';
  const blockedTitle = pending ? t('envTab.blocked') : !detail.exists ? t('credentials.needsEnvironment') : undefined;
  return (
    <div className="space-y-6">
      <EnvironmentBanner env={env} />
      <VnnicPresetPanel connection={c} env={env} detail={detail} onSetUp={() => setAdding('preset')} />
      <Panel
        title={t('certificates.title')}
        description={t('certificates.description')}
        actions={
          <>
            <Button size="sm" onClick={() => setAdding('pkcs12')} disabled={cannotAdd} title={blockedTitle}>
              <FileKey2 /> {t('pkcs12.open')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setAdding('pem')} disabled={cannotAdd} title={blockedTitle}>
              <Upload /> {t('certificates.add')}
            </Button>
          </>
        }
      >
        {!detail.exists ? <p className="text-sm text-muted-foreground">{t('credentials.needsEnvironment')}</p> : null}
        {pending ? <p className="mb-3 text-xs text-muted-foreground">{t('envTab.blocked')}</p> : null}
        {certs.length === 0 ? (
          <EmptyState title={t('certificates.none')}>{env === 'PRODUCTION' ? t('certificates.requiredProduction') : t('certificates.noneHint')}</EmptyState>
        ) : (
          <ul className="grid gap-3">
            {certs.map((x) => (
              <li key={x.version} className={cn('space-y-3 rounded-lg border p-4', x.status === 'ACTIVE' && 'border-success/40')}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{t('versions.versionN', { version: x.version })}</span>
                    <StatusBadge group="credentialStatus" value={x.status} />
                    <ApprovalNote item={x} />
                    {x.status !== 'REVOKED' ? (
                      x.days_remaining < 0 ? (
                        <Badge tone="crit">{t('certificates.expired')}</Badge>
                      ) : (
                        <Badge tone={x.days_remaining < 30 ? 'warn' : 'ok'}>{t('certificates.daysLeft', { count: x.days_remaining })}</Badge>
                      )
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {x.status !== 'REVOKED' ? (
                      <Button size="xs" variant="outline" onClick={() => setAction({ kind: 'revoke', item: x })}>
                        <Ban /> {t('credentials.revoke')}
                      </Button>
                    ) : null}
                    {x.private_key.source === 'STORED' && x.private_key.stored_secret_id && !x.private_key.destroyed && (x.status === 'ROTATED' || x.status === 'REVOKED') ? (
                      <Button size="xs" variant="ghost" onClick={() => setAction({ kind: 'destroy', id: x.private_key.stored_secret_id ?? '', what: t('certificates.destroyWhat', { version: x.version }) })}>
                        <Trash2 /> {t('credentials.destroy')}
                      </Button>
                    ) : null}
                  </div>
                </div>
                <ClosedNote item={x} />
                <KeyValue
                  columns={2}
                  rows={[
                    [t('certificates.subject'), <span key="s" className="break-all">{x.subject}</span>],
                    [t('certificates.issuer'), <span key="i" className="break-all">{x.issuer}</span>],
                    [t('certificates.validity'), `${formatDateTime(x.not_before)} → ${formatDateTime(x.not_after)}`],
                    [t('certificates.serial'), <code key="se" className="break-all text-xs">{x.serial}</code>],
                    [t('certificates.fingerprint'), <CopyText key="f" value={x.fingerprint_sha256} display={`${x.fingerprint_sha256.slice(0, 16)}…`} />],
                    [t('certificates.key'), <SecretStatusLine key="k" status={x.private_key} />],
                  ]}
                />
                <p className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                  {t('versions.createdBy')}: {x.created_by ? <StaffName staff={x.created_by} /> : t('common.outsideControlCenter')} · <When at={x.created_at} />
                  {x.reason ? ` · ${x.reason}` : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <Panel
        title={t('rootCa.title')}
        description={t('rootCa.description')}
        actions={
          <Button size="sm" variant="outline" onClick={() => setAdding('rootCa')} disabled={!detail.exists || c.status === 'RETIRED'} title={!detail.exists ? t('credentials.needsEnvironment') : undefined}>
            <Upload /> {caCerts.length ? t('rootCa.replace') : t('rootCa.add')}
          </Button>
        }
      >
        {caCerts.length === 0 && chainCas.length > 0 ? (
          <Callout tone="info" className="mb-3" title={t('rootCa.fromChainTitle', { count: chainCas.length })}>
            <p>{t('rootCa.fromChainBody')}</p>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => setFromChain(true)} disabled={c.status === 'RETIRED'}>
              {t('rootCa.fromChain')}
            </Button>
          </Callout>
        ) : null}
        {caCerts.length === 0 ? (
          <EmptyState title={t('rootCa.none')}>{t('rootCa.noneHint')}</EmptyState>
        ) : (
          <ul className="grid gap-3">
            {caCerts.map((x) => (
              <li key={x.sha256} className="space-y-2 rounded-lg border p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold break-all">{x.subject}</span>
                  <Badge tone="neutral">{x.self_signed ? t('rootCa.root') : t('rootCa.intermediate')}</Badge>
                  {x.days_remaining < 0 ? <Badge tone="crit">{t('certificates.expired')}</Badge> : <Badge tone={x.days_remaining < 30 ? 'warn' : 'ok'}>{t('certificates.daysLeft', { count: x.days_remaining })}</Badge>}
                </div>
                <KeyValue
                  columns={2}
                  rows={[
                    [t('certificates.issuer'), <span key="i" className="break-all">{x.issuer}</span>],
                    [t('certificates.validity'), `${formatDateTime(x.not_before)} → ${formatDateTime(x.not_after)}`],
                    [t('certificates.fingerprint'), <CopyText key="f" value={x.sha256} display={`${x.sha256.slice(0, 16)}…`} />],
                  ]}
                />
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <RootCaDialog open={fromChain} onOpenChange={setFromChain} connectionId={c.id} connectionName={c.name} env={env} initialPem={chainCas.join('\n')} onDone={refresh} />
      <RootCaDialog open={adding === 'rootCa'} onOpenChange={(o) => setAdding(o ? 'rootCa' : null)} connectionId={c.id} connectionName={c.name} env={env} onDone={refresh} />
      <CertificateDialog open={adding === 'pem'} onOpenChange={(o) => setAdding(o ? 'pem' : null)} connection={c} env={env} detail={detail} onDone={refresh} />
      <Pkcs12Dialog open={adding === 'pkcs12'} onOpenChange={(o) => setAdding(o ? 'pkcs12' : null)} connection={c} env={env} detail={detail} onDone={refresh} />
      <EnvironmentDialog open={adding === 'preset'} onOpenChange={(o) => setAdding(o ? 'preset' : null)} connection={c} env={env} detail={detail} section="endpoint" startFromPreset onDone={refresh} />
      {action?.kind === 'revoke' ? (
        <RevokeDialog open onOpenChange={(o) => !o && setAction(null)} what="certificate" item={action.item} connectionId={c.id} env={env} onDone={refresh} />
      ) : null}
      {action?.kind === 'destroy' ? <DestroySecretDialog open onOpenChange={(o) => !o && setAction(null)} storedSecretId={action.id} what={action.what} onDone={refresh} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// History

export function HistoryTab({ connection: c, env }: TabProps) {
  const { t } = useTranslation('integrations');
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  const history = useApi((signal) => api.listIntegrationHistory({ resource_type: 'registry_connection', resource_id: c.id, before: cursor, limit: 25 }, { signal }), [c.id, cursor]);
  const tests = useApi((signal) => api.listConnectionTests({ connection_id: c.id, limit: 10 }, { signal }), [c.id]);
  const data = useRetained(history.data);
  return (
    <div className="space-y-6">
      <Panel title={t('history.title')} description={t('history.description')}>
        {history.status === 'error' ? (
          <DataError error={history.error} onRetry={history.reload} />
        ) : !data ? (
          <Loading rows={3} />
        ) : data.items.length === 0 ? (
          <EmptyState title={t('history.none')} />
        ) : (
          <ol className="grid gap-2">
            {data.items.map((h) => (
              <li key={h.id} className="space-y-1.5 rounded-md border px-3 py-2">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  {h.outcome === 'SUCCESS' ? (
                    <CircleCheck className="size-4 text-success" aria-label={t('history.outcome.SUCCESS')} />
                  ) : h.outcome === 'FAILED' ? (
                    <CircleX className="size-4 text-critical" aria-label={t('history.outcome.FAILED')} />
                  ) : (
                    <CircleMinus className="size-4 text-warning" aria-label={t('history.outcome.DENIED')} />
                  )}
                  <span className="font-medium">{tx(`history.action.${h.action.replace(/\./g, '_')}`, h.action)}</span>
                  {/* Entries from before ADR-018 may concern another server's (now archived) settings. */}
                  {h.environment && h.environment !== env ? <EnvBadge env={h.environment} /> : null}
                  <span className="text-xs text-muted-foreground">
                    <When at={h.at} /> · {h.actor.type === 'OPERATOR' ? h.actor.user.display_name : tx(`history.actor.${h.actor.type}`, h.actor.id)}
                  </span>
                  {h.approval_request_id ? <Badge tone="pending" icon={false}>{t('history.withApproval')}</Badge> : null}
                </div>
                <FieldChanges changes={h.changes} compact />
                {h.reason ? (
                  <p className="text-xs">
                    <span className="text-muted-foreground">{t('versions.reason')}:</span> {h.reason}
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
        )}
        {data && (data.next_before || cursors.length) ? (
          <div className="mt-3 flex justify-end gap-2">
            <Button size="sm" variant="outline" disabled={!cursors.length} onClick={() => setCursors((x) => x.slice(0, -1))}>
              {t('common.newer')}
            </Button>
            <Button size="sm" variant="outline" disabled={!data.next_before} onClick={() => data.next_before && setCursors((x) => [...x, data.next_before as string])}>
              {t('common.older')}
            </Button>
          </div>
        ) : null}
      </Panel>
      <Panel title={t('envTab.tests')} description={t('envTab.testsHint')}>
        {tests.status === 'error' ? <DataError error={tests.error} onRetry={tests.reload} /> : tests.status === 'loading' ? <Loading rows={2} /> : <TestList tests={tests.data.items} />}
      </Panel>
    </div>
  );
}
